import React, { useCallback, useEffect, useState } from 'react';
import { Close } from '@mui/icons-material';
import {
  Chip, IconButton, InputAdornment, Popover,
  Collapse, Divider
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Box } from '@mui/system';
import { A_ARC } from '@lba-dev/package.local-globals/aStatus';

import ContactList from './ContactList';
import { smsTypes, getPinned } from '../constant';
import CustomTextField from '../../CustomInputs/CustomTextField';
import {
  getOldConv, searchConversations
} from '../../../actions/conversations';
import { checkPhoneNumber } from '../../../utils/function';
import notifSystem from '../../../notifSystem';
import CustomLoadingButton from '../../Utils/CustomLoadingButton';
import collections from '../../../constants/collections';

const useStyles = makeStyles()(() => ({
  popup: {
    width: 430,
    maxHeight: 'calc(100% - 115px)',
    overflow: 'hidden',
    top: 64,
    height: '100%'
  },
  adornmentDivider: {
    height: 28,
    width: 1,
    paddingLeft: 4
  }
}));

const genContact = (oldConv, user, text, hasWhatsapp) => {
  const {
    sender = true,
    transmitter = user.login,
    recipient = `${text} - ${user.alias}`,
    date = new Date(),
    text: message = '',
    artisanObject, interObject, devisObject,
    mode = 0,
    intervention, devis, artisan,
    seenBy = []
  } = oldConv || {};
  return {
    'companyName': sender ? recipient : transmitter,
    'login': transmitter,
    'date': new Date(date),
    'dataObject': artisanObject || interObject || devisObject || {},
    'mode': mode || 0,
    '_id': artisan || intervention || devis || text,
    'type': (artisan && 'artisan') ||
        (devis && 'devis') || 'intervention',
    message,
    'unSeenCount': 0,
    'seenBy': seenBy || [],
    'sizeOfCommercial': 1,
    newContact: oldConv ? !!oldConv.newContact : true,
    freshContact: !oldConv,
    hasWhatsapp
  };
};

const ContactPopover = ({
  anchorEl, setAnchorEl,
  selected, setSelected,
  user, smsType, pinConversation
}) => {
  const { classes } = useStyles();
  const [text, setText] = useState('');
  const [isNumber, setIsNumber] = useState(false);
  const [loading, setLoading] = useState(false);
  const hasWhatsapp = true;
  const [contacts, setContacts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [filter, setFilter] = useState('');
  const [buttonLoading, setButtonLoading] = useState(false);

  const callBackIsNumber = useCallback((isTel) => {
    setIsNumber(isTel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNumber]);


  const checkNumber = (value) => {
    const isTel = smsType === 'intervention' &&
    checkPhoneNumber(value);
    callBackIsNumber(isTel, value);
  };

  const searchContact = (input) => {
    setLoading(true);
    Promise.all([
      ...smsTypes.filter(e => !['all', 'allPinned'].includes(e.type))
        .map(e => e.collection), 'devis'
    ].map(e => (
      searchConversations(input, e, e === collections.artisans ? 20 : 10)
    )))
      .then(([artisans, ...data]) => {
        const artisanPinned = getPinned('artisan', user.pinnedConversations)
          .map(e => e._id);
        artisans = artisans.filter(e => +e.status !== A_ARC)
          .sort((a, b) => +a.status - +b.status)
          .sort((a, b) =>
            artisanPinned.includes(b._id) - artisanPinned.includes(a._id));
        data = [artisans, ...data].flat();
        setContacts(data);
        setFiltered(data);
        if (!data.length) {
          checkNumber(input);
        }
      })
      .finally(() => setLoading(false));
    checkNumber(input);
  };


  const updateSearch = value => {
    setContacts([]);
    setFiltered([]);
    value = value.replace(/\n/g, '');
    if (value.length >= 3) {
      searchContact(value);
    }
    setText(value);
  };

  useEffect(() => {
    if (filter) {
      const nFiltered = [...contacts].filter(e => e.collection === filter);
      setFiltered(nFiltered);
    } else {
      setFiltered([...contacts]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const closePopup = () => {
    setAnchorEl(null);
    setText('');
    setContacts([]);
    setFilter([]);
    setIsNumber(false);
  };

  const selectContact = (item) => {
    setSelected(item);
    closePopup();
  };

  const createContact = async () => {
    const isTel = checkPhoneNumber(text);
    if (!isTel) {
      notifSystem.error('Erreur',
        'Veuiller saisir un numero mobile (06, 07 ou mobile étranger).');
    }
    setButtonLoading(true);
    const oldConv = await getOldConv(text)
      .finally(() => setButtonLoading(false));
    const contact = genContact(oldConv, user, text, hasWhatsapp);
    selectContact(contact);
  };
  return <Popover
    id="contactSearch"
    open={!!anchorEl}
    anchorEl={anchorEl}
    anchorOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    PaperProps={{ className: classes.popup }}
    sx={{ left: 20 }}
    onClose={closePopup}
  >
    <CustomTextField
      placeholder={`${smsTypes.map(e => e.title).join(', ')}`}
      autoFocus
      variant='outlined'
      value={text}
      path={[]}
      setData={(path, value) => updateSearch(value)}
      capsLocked={false}
      texttransform='unset'
      InputProps={{
        endAdornment: <InputAdornment position="end">
          {isNumber ? <>
            <Divider
              orientation="vertical"
              className={classes.adornmentDivider}
            />
          </> : ''}
          <IconButton onClick={closePopup}>
            <Close fontSize='small' />
          </IconButton>
        </InputAdornment>
      }}
    />
    {contacts.length ?
      <Box display="flex" justifyContent='space-evenly' p={1}>
        <Chip
          label="Tous"
          color="secondary"
          size="small"
          variant={!filter ? 'filled' : 'outlined'}
          onClick={() => setFilter('')}
        />
        {smsTypes.filter(e => !['all', 'allPinned'].includes(e.type))
          .map((e) => {
            const count = [...contacts]
              .filter(f => f.collection === e.collection).length;
            return count ? <Chip
              key={`${e.type}-${e.title}`}
              label={e.type.capitalize()}
              color="primary"
              size="small"
              variant={filter === e.collection ? 'filled' : 'outlined'}
              onClick={() => setFilter(e.collection)}
            /> : '';
          })}
      </Box> : ''}
    <Collapse in={isNumber && !contacts.length}>
      <Box sx={{ p: 1 }}>
        <CustomLoadingButton
          variant='contained'
          fullWidth
          disabled={!isNumber}
          color='primary'
          loading={buttonLoading}
          onClick={createContact}>
          Envoyer un message
        </CustomLoadingButton>
      </Box>
    </Collapse>
    <ContactList
      isSearch
      contacts={filtered}
      selected={selected}
      setSelected={selectContact}
      user={user}
      smsType={smsType}
      loading={loading}
      pinConversation={pinConversation}
    />
  </Popover>;

};

export default ContactPopover;
